import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _keys from 'lodash/keys';
import _mapValues from 'lodash/mapValues';
import _omit from 'lodash/omit';
import _pickBy from 'lodash/pickBy';

interface SchemaWithProperties {
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * a theme-overridden variant field has shape `{ type: 'string', oneOf: [...] }`
 * (see themeVariantsSchema.ts variantDimensionToSchema). when every option is
 * struck off, oneOf becomes []. the `type === 'string'` guard distinguishes a
 * theme-emptied variant from an authored object schema that happens to expose
 * an empty oneOf (e.g. directional style schemas) — those must NOT be stripped.
 */
const isHiddenVariantField = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false;
  const typed = value as { type?: unknown; oneOf?: unknown };
  return typed.type === 'string' && _isArray(typed.oneOf) && _isEmpty(typed.oneOf);
};

type StripResult = { changed: false } | { changed: true; schema: object; uiSchema?: object };

/**
 * recurses into type:object children to strip empty-oneOf fields at every
 * nesting level. uiSchema descends WITHOUT a `properties` key — RJSF uiSchema
 * mirrors the data shape, not the JSON-Schema shape. so
 * `schema.properties.foo.properties.bar` corresponds to `uiSchema.foo.bar`.
 *
 * bail-out contract: returns `{ changed: false }` when nothing changed in the
 * subtree. callers that see `changed: false` must reuse the input refs they
 * already hold — preserves downstream useMemo identity in useBlockTabDetails.
 */
function stripRecursive(
  schema: SchemaWithProperties | undefined,
  uiSchema: Record<string, unknown> | undefined,
): StripResult {
  const properties = schema?.properties;
  if (!properties) return { changed: false };

  const directHiddenKeys = _keys(_pickBy(properties, isHiddenVariantField));

  const childResults: Record<string, Extract<StripResult, { changed: true }>> = {};
  let anyNestedChanged = false;

  for (const key of _keys(properties)) {
    if (directHiddenKeys.includes(key)) continue;
    const child = properties[key] as SchemaWithProperties | undefined;
    if (child?.type === 'object' && child.properties) {
      const childUiSchema = uiSchema?.[key] as Record<string, unknown> | undefined;
      const childResult = stripRecursive(child, childUiSchema);
      if (childResult.changed) {
        childResults[key] = childResult;
        anyNestedChanged = true;
      }
    }
  }

  if (directHiddenKeys.length === 0 && !anyNestedChanged) {
    return { changed: false };
  }

  const replacedProperties = _omit(
    {
      ...properties,
      ..._mapValues(childResults, (result) => result.schema),
    },
    directHiddenKeys,
  );

  // when a nested subtree changed but its uiSchema branch was absent, the child
  // result carries `uiSchema: undefined` — filter those out so we don't leak a
  // phantom `key: undefined` into the merged uiSchema.
  const childUiResults = _pickBy(childResults, (result) => result.uiSchema !== undefined);
  const replacedUiSchema = uiSchema
    ? _omit(
        {
          ...uiSchema,
          ..._mapValues(childUiResults, (result) => result.uiSchema),
        },
        directHiddenKeys,
      )
    : uiSchema;

  return {
    changed: true,
    schema: { ...(schema as object), properties: replacedProperties },
    uiSchema: replacedUiSchema,
  };
}

/**
 * strips properties whose theme-derived schema arrived with an empty oneOf —
 * every option struck off in the theme manager. operates recursively into
 * nested type:object containers (e.g. `tabItemAppearance.weight`). removes each
 * key from both `schema.properties` and `uiSchema` so RJSF, tab-visibility
 * checks, and component-overrides surfaces all auto-skip the field.
 *
 * referential stability is FIELD-LEVEL: when nothing changes in the subtree,
 * the returned `schema` and `uiSchema` match input refs. The outer
 * `{ schema, uiSchema }` wrapper itself is always a fresh allocation —
 * consumers should compare the field refs (not the wrapper) to detect change.
 */
export function stripEmptyOneOfFields({
  schema,
  uiSchema,
}: {
  schema?: object;
  uiSchema?: object;
} = {}): { schema?: object; uiSchema?: object } {
  const result = stripRecursive(
    schema as SchemaWithProperties | undefined,
    uiSchema as Record<string, unknown> | undefined,
  );
  return result.changed
    ? { schema: result.schema, uiSchema: result.uiSchema }
    : { schema, uiSchema };
}
